import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const AnimatedSlider = ({ photos }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationType, setAnimationType] = useState('fade');

  const animations = ['fade', 'slide', 'zoom', 'blur', 'rotate'];

  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [currentIndex]);

  const nextSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setAnimationType(animations[Math.floor(Math.random() * animations.length)]);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % photos.length);
      setIsAnimating(false);
    }, 800);
  };

  const prevSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setAnimationType(animations[Math.floor(Math.random() * animations.length)]);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length);
      setIsAnimating(false);
    }, 800);
  };

  const goToSlide = (index) => {
    if (isAnimating || index === currentIndex) return;
    setIsAnimating(true);
    setAnimationType(animations[Math.floor(Math.random() * animations.length)]);
    setTimeout(() => {
      setCurrentIndex(index);
      setIsAnimating(false);
    }, 800);
  };

  const getAnimationClass = () => {
    if (!isAnimating) return '';
    switch (animationType) {
      case 'fade':
        return 'animate-fade-in';
      case 'slide':
        return 'animate-slide-in';
      case 'zoom':
        return 'animate-zoom-in';
      case 'blur':
        return 'animate-blur-in';
      case 'rotate':
        return 'animate-rotate-in';
      default:
        return '';
    }
  };

  if (!photos || photos.length === 0) return null;

  return (
    <div className="relative w-full h-[70vh] md:h-[80vh] overflow-hidden bg-black group">
      {/* Main Image */}
      <div className="relative w-full h-full">
        <img
          src={photos[currentIndex].url}
          alt={photos[currentIndex].title}
          className={`w-full h-full object-cover ${getAnimationClass()}`}
          style={{ objectPosition: 'center' }}
        />
        
        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black opacity-60"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-black/50"></div>
      </div>

      {/* Title overlay */}
      <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
        <div className="text-center px-4">
          <h2 className="text-5xl md:text-7xl font-black text-white mb-4 drop-shadow-2xl tracking-tight animate-fade-in">
            PROFESSIONAL BODY PIERCING
          </h2>
          <p className="text-xl md:text-2xl text-zinc-200 font-semibold drop-shadow-lg">
            Expert Artistry • 20+ Years Experience
          </p>
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-30 bg-black/50 hover:bg-red-800 text-white p-4 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100 hover:scale-110 backdrop-blur-sm"
        aria-label="Previous slide"
      >
        <ChevronLeft className="h-8 w-8" />
      </button>
      
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-30 bg-black/50 hover:bg-red-800 text-white p-4 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100 hover:scale-110 backdrop-blur-sm"
        aria-label="Next slide"
      >
        <ChevronRight className="h-8 w-8" />
      </button>

      {/* Pagination Dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex gap-3">
        {photos.slice(0, Math.min(photos.length, 10)).map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`transition-all duration-300 rounded-full ${
              index === currentIndex
                ? 'bg-red-800 w-12 h-3'
                : 'bg-white/50 hover:bg-white w-3 h-3 hover:scale-125'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          ></button>
        ))}
      </div>

      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-zinc-800 z-30">
        <div
          className="h-full bg-red-800 transition-all duration-5000 ease-linear"
          style={{ width: `${((currentIndex + 1) / photos.length) * 100}%` }}
        ></div>
      </div>
    </div>
  );
};

export default AnimatedSlider;
